import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { setupAxiosInterceptors } from './components/axiosSetup';
import { applyServerConfig } from './config';

const AppContext = createContext({
    wartung: null,
    lizenzFehler: null,
    features: {},
    appName: 'Fasnet.App',
    version: '',
    isOn: (_key) => true,
    licenseStatus: 'missing',
    licensedModules: null,
});

export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
    const navigate = useNavigate();
    const [wartung, setWartung]             = useState(null);
    const [lizenzFehler, setLizenzFehler]   = useState(null);
    const [features, setFeatures]           = useState({});
    const [appName, setAppName]             = useState('Fasnet.App');
    const [version, setVersion]             = useState('');
    const [licenseStatus, setLicenseStatus] = useState('missing');
    // Sperrzustände und Ablaufinfos für Hinweisbanner und Login-Sperre
    const [lizenz, setLizenz] = useState({
        enforced: false, blocked: false, daysLeft: null, expDate: null,
        systemLocked: false, systemLockedMessage: '',
    });
    const [licensedModules, setLicensedModules] = useState(null);

    useEffect(() => {
        setupAxiosInterceptors(navigate);

        // Bypass-Secret aus URL in sessionStorage sichern
        const params = new URLSearchParams(window.location.search);
        const bypassParam = params.get('bypass');
        if (bypassParam) {
            sessionStorage.setItem('wartungBypass', bypassParam);
            params.delete('bypass');
            window.history.replaceState({}, '', window.location.pathname + (params.toString() ? '?' + params.toString() : ''));
        }

        const bypass = bypassParam || sessionStorage.getItem('wartungBypass');
        const url = bypass
            ? `/api/config/public?bypass=${encodeURIComponent(bypass)}`
            : '/api/config/public';

        fetch(url)
            .then(r => r.json())
            .then(cfg => {
                // Zuerst die Vereinsdaten übernehmen: App.jsx rendert erst,
                // wenn wartung nicht mehr null ist — danach stehen die Werte
                // allen Komponenten zur Verfügung.
                applyServerConfig(cfg);

                setWartung(
                    cfg.Wartung === '1'
                        ? (cfg.WartungMessage || 'Der Server wird derzeit gewartet. Bitte versuche es später erneut.')
                        : false
                );
                if (cfg.AppName) {
                    setAppName(cfg.AppName);
                    // Der Titel in der index.html steht beim Build fest. Hier
                    // nachziehen, damit Browser-Tab und Lesezeichen denselben
                    // Namen tragen wie die App selbst.
                    document.title = cfg.AppName;
                }
                if (cfg.Version) setVersion(cfg.Version);

                // Lizenz-Infos
                if (cfg._licenseStatus) setLicenseStatus(cfg._licenseStatus);
                setLizenz({
                    enforced:            !!cfg._licenseEnforced,
                    blocked:             !!cfg._loginBlocked,
                    daysLeft:            typeof cfg._licenseDaysLeft === 'number' ? cfg._licenseDaysLeft : null,
                    expDate:             cfg._licenseExpDate || null,
                    status:              cfg._licenseStatus || 'missing',
                    customer:            cfg._licenseCustomer || '',
                    systemLocked:        !!cfg._systemLocked,
                    systemLockedMessage: cfg._systemLockedMessage || '',
                });
                setLicensedModules(cfg._licensedModules ?? null);

                // Früherkennung: Lizenzfehler bereits beim Laden der Config melden
                if (cfg._licenseEnforced && ['missing', 'invalid'].includes(cfg._licenseStatus)) {
                    setLizenzFehler({
                        status: cfg._licenseStatus,
                        message: cfg._licenseStatus === 'missing'
                            ? 'Kein Lizenzschlüssel konfiguriert.'
                            : 'Der konfigurierte Lizenzschlüssel ist ungültig.',
                    });
                }

                const f = {};
                Object.entries(cfg).forEach(([k, v]) => {
                    if (k.startsWith('Modul_') || k.startsWith('Feature_')) f[k] = v === '1';
                });
                setFeatures(f);
            })
            .catch(() => {
                // Netzwerkfehler → Wartungsseite zeigen (fail-safe)
                setWartung('Verbindung zum Server konnte nicht hergestellt werden.');
            });

        const wartungHandler = (e) =>
            setWartung(e.detail?.message || 'Der Server wird derzeit gewartet. Bitte versuche es später erneut.');
        window.addEventListener('nzapp:wartung', wartungHandler);

        const lizenzHandler = (e) => setLizenzFehler(e.detail || { status: 'missing', message: '' });
        window.addEventListener('nzapp:lizenzfehler', lizenzHandler);

        return () => {
            window.removeEventListener('nzapp:wartung', wartungHandler);
            window.removeEventListener('nzapp:lizenzfehler', lizenzHandler);
        };
    }, [navigate]);

    // isOn gibt true zurück wenn Modul aktiv ODER nicht konfiguriert (default: an)
    const isOn = (key) => (key in features) ? features[key] === true : true;

    return (
        <AppContext.Provider value={{ wartung, lizenzFehler, features, appName, version, isOn, licenseStatus, licensedModules, lizenz }}>
            {children}
        </AppContext.Provider>
    );
};
